<script setup>
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

// v-model: array of connector objects stored in the bot's config.
// Shape (future-proof): { id, type, url, mode, retrieval, status }
//   type:      'google_drive' | 'website' | 'file' | 'database'
//   mode:      google_drive only -> 'url' (paste a shared link) | 'create'
//              (system creates a Drive folder, not implemented yet)
//   retrieval: 'rag' | 'scrape' | 'query'  (how the bot will use the source)
//   status:    'pending'  (ingestion not implemented yet)
const connectors = defineModel({ type: Array, default: () => [] });

const { t } = useI18n();

const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

// Source types. `available` ones can be added now (just capture config);
// the rest are future-proofed placeholders shown as "coming soon".
const SOURCE_TYPES = [
  { type: 'website', icon: 'i-lucide-globe', available: true },
  { type: 'google_drive', icon: 'i-lucide-hard-drive', available: true },
  { type: 'file', icon: 'i-lucide-file', available: false },
  { type: 'database', icon: 'i-lucide-database', available: false },
];

const iconFor = type =>
  SOURCE_TYPES.find(s => s.type === type)?.icon || 'i-lucide-plug';

const addConnector = source => {
  if (!source.available) return;
  const base = { id: uid(), type: source.type, url: '', status: 'pending' };
  if (source.type === 'website') {
    base.retrieval = 'scrape';
  } else if (source.type === 'google_drive') {
    base.mode = 'url';
    base.retrieval = 'rag';
  }
  connectors.value = [...connectors.value, base];
};

const updateConnector = (id, patch) => {
  connectors.value = connectors.value.map(c =>
    c.id === id ? { ...c, ...patch } : c
  );
};

const removeConnector = id => {
  connectors.value = connectors.value.filter(c => c.id !== id);
};

const setDriveMode = (id, mode) => {
  updateConnector(id, mode === 'create' ? { mode, url: '' } : { mode });
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-1">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CHATBOTS.CONNECTORS.TITLE') }}
      </span>
      <span class="text-xs text-n-slate-11">
        {{ t('CHATBOTS.CONNECTORS.DESCRIPTION') }}
      </span>
    </div>

    <!-- Add-source buttons -->
    <div class="flex flex-wrap gap-2">
      <button
        v-for="source in SOURCE_TYPES"
        :key="source.type"
        type="button"
        :disabled="!source.available"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-dashed border-n-weak text-n-slate-12 hover:border-n-strong disabled:opacity-50 disabled:cursor-not-allowed"
        @click="addConnector(source)"
      >
        <span :class="source.icon" class="size-3.5" />
        {{ t(`CHATBOTS.CONNECTORS.TYPES.${source.type.toUpperCase()}`) }}
        <span
          v-if="!source.available"
          class="ml-1 px-1 py-0.5 rounded bg-n-alpha-2 text-n-slate-10 text-[10px]"
        >
          {{ t('CHATBOTS.CONNECTORS.COMING_SOON') }}
        </span>
      </button>
    </div>

    <!-- Configured connectors -->
    <div v-if="connectors.length" class="flex flex-col gap-2">
      <div
        v-for="connector in connectors"
        :key="connector.id"
        class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak bg-n-solid-1"
      >
        <div class="flex items-center gap-2">
          <span
            :class="iconFor(connector.type)"
            class="size-4 text-n-slate-10 flex-shrink-0"
          />
          <span class="text-sm font-medium text-n-slate-12 flex-1">
            {{ t(`CHATBOTS.CONNECTORS.TYPES.${connector.type.toUpperCase()}`) }}
          </span>
          <Button
            v-tooltip="t('CHATBOTS.CONNECTORS.REMOVE')"
            variant="ghost"
            color="ruby"
            size="xs"
            icon="i-lucide-trash-2"
            @click="removeConnector(connector.id)"
          />
        </div>

        <!-- Website: just a URL -->
        <Input
          v-if="connector.type === 'website'"
          :model-value="connector.url"
          :placeholder="t('CHATBOTS.CONNECTORS.WEBSITE_PLACEHOLDER')"
          class="w-full"
          @update:model-value="updateConnector(connector.id, { url: $event })"
        />

        <!-- Google Drive: paste a shared link OR create a folder -->
        <template v-else-if="connector.type === 'google_drive'">
          <div class="flex gap-2">
            <button
              type="button"
              class="flex-1 px-3 py-1.5 rounded-lg text-xs border"
              :class="
                connector.mode === 'url'
                  ? 'border-n-brand text-n-brand bg-n-brand/5'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="setDriveMode(connector.id, 'url')"
            >
              {{ t('CHATBOTS.CONNECTORS.DRIVE.MODE_URL') }}
            </button>
            <button
              type="button"
              class="flex-1 px-3 py-1.5 rounded-lg text-xs border"
              :class="
                connector.mode === 'create'
                  ? 'border-n-brand text-n-brand bg-n-brand/5'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="setDriveMode(connector.id, 'create')"
            >
              {{ t('CHATBOTS.CONNECTORS.DRIVE.MODE_CREATE') }}
            </button>
          </div>

          <Input
            v-if="connector.mode === 'url'"
            :model-value="connector.url"
            :placeholder="t('CHATBOTS.CONNECTORS.DRIVE.URL_PLACEHOLDER')"
            class="w-full"
            @update:model-value="updateConnector(connector.id, { url: $event })"
          />
          <div
            v-else
            class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-alpha-black2 text-xs text-n-slate-11"
          >
            <span class="i-lucide-folder-plus size-4 text-n-slate-10" />
            {{ t('CHATBOTS.CONNECTORS.DRIVE.CREATE_PLACEHOLDER') }}
          </div>
        </template>
      </div>
    </div>
    <span v-else class="text-xs text-n-slate-10">
      {{ t('CHATBOTS.CONNECTORS.EMPTY') }}
    </span>
  </div>
</template>
